import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import { AreaLineChart } from '../common/AreaLineChart';
import { expensesAPI } from '../../data/expenses';
import { unwrap } from '../../data/api';
import { expensesKeys } from '../../features/expenses/queries';
import pt from '../../locales/pt';
import { useSelfieCycle } from '../../store/SelfieCycleContext';
import { formatEUR } from '../../utils/money';
import { formatSelfiePeriodLabel } from '../../utils/selfiePeriod';

const MAX_CYCLES = 8;

function formatCycleAxisLabel(startISO, endISO) {
  const start = new Date(`${startISO}T12:00:00`);
  const end = new Date(`${endISO}T12:00:00`);
  const sameMonth =
    start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  const dayFmt = new Intl.DateTimeFormat('pt-PT', { day: 'numeric' });
  const monthFmt = new Intl.DateTimeFormat('pt-PT', { month: 'short' });
  if (sameMonth) {
    return `${dayFmt.format(start)}–${dayFmt.format(end)} ${monthFmt.format(end)}`;
  }
  return formatSelfiePeriodLabel(startISO, endISO);
}

/**
 * @param {{ expenses: number }[]} points
 * @param {number} cycleCount
 */
export function hasExpenseTimelineData(points, cycleCount) {
  return cycleCount >= 2 && (points || []).some((row) => Number(row?.expenses) > 0);
}

/**
 * Totais de despesa por ciclo selfie fechado (até `maxCycles`, exclui o ciclo actual).
 */
export function useExpenseTimelineSeries({ maxCycles = MAX_CYCLES } = {}) {
  const { cycles = [] } = useSelfieCycle();

  const chartCycles = useMemo(() => {
    const closed = (cycles || []).filter((cycle) => !cycle.isCurrent);
    const recent = closed.slice(0, maxCycles);
    return [...recent].reverse();
  }, [cycles, maxCycles]);

  const results = useQueries({
    queries: chartCycles.map((cycle) => {
      const filters = {
        occurred_on_from: cycle.start,
        occurred_on_to: cycle.end,
      };
      return {
        queryKey: expensesKeys.totals(filters),
        queryFn: () => expensesAPI.totals(filters).then((r) => unwrap(r)),
        enabled: Boolean(cycle.start && cycle.end),
        staleTime: 5 * 60 * 1000,
      };
    }),
  });

  const data = useMemo(
    () =>
      chartCycles.map((cycle, idx) => ({
        id: `${cycle.start}-${cycle.end}`,
        label: formatCycleAxisLabel(cycle.start, cycle.end),
        fullLabel: cycle.label || formatSelfiePeriodLabel(cycle.start, cycle.end),
        expenses: Number(results[idx]?.data?.total || 0),
        isCurrent: Boolean(cycle.isCurrent),
      })),
    [chartCycles, results],
  );

  const isLoading = results.some((r) => r.isLoading || r.isPending);
  const isError = results.some((r) => r.isError);
  const hasData = hasExpenseTimelineData(data, chartCycles.length);

  const avg = useMemo(() => {
    const valid = data.filter((d) => d.expenses > 0);
    if (valid.length < 2) return 0;
    return valid.reduce((sum, d) => sum + d.expenses, 0) / valid.length;
  }, [data]);

  const refetch = () => results.forEach((r) => r.refetch?.());

  return { chartCycles, data, isLoading, isError, hasData, avg, refetch };
}

/**
 * Só os extremos vão para o eixo — o `.pen` mostra uma etiqueta por ponto, mas
 * as etiquetas de ciclo («28/03 – 27/04») são largas demais para 8 pontos a 375.
 */
function axisLabelsFor(data) {
  if (data.length <= 1) return data.map((row) => row.label);
  return [data[0].label, data[data.length - 1].label];
}

/**
 * Vista do gráfico (sem fetch). Assume dados prontos.
 *
 * `.pen` → `ds/chart/area` (`AQ6R9`). `variant="plain"`: os pontos de uso
 * (`ExpenseTimelineMiniCard`, painéis de relatório) já trazem cartão e
 * cabeçalho próprios, por isso aqui entra só o miolo do master.
 */
export function ExpenseTimelineChartView({ data, className, avg = 0 }) {
  const points = (data || []).map((row) => ({
    id: row.id,
    label: row.label,
    value: Number(row.expenses || 0),
  }));

  return (
    <AreaLineChart
      variant="plain"
      data={points}
      axisLabels={axisLabelsFor(points)}
      markers="all"
      referenceValue={avg}
      referenceLabel={avg > 0 ? `${formatEUR(avg, { compact: true })}/mês` : undefined}
      tooltipLabel={pt.dashboard.charts.expenseEvolutionLabel}
      emptyLabel={pt.dashboard.charts.expenseEvolutionEmpty}
      className={className}
    />
  );
}

/**
 * Evolução do total de despesas ao longo dos ciclos selfie.
 */
export function ExpenseTimelineChart({ className, maxCycles = MAX_CYCLES }) {
  const { chartCycles, data, isLoading, isError, hasData, avg, refetch } =
    useExpenseTimelineSeries({ maxCycles });

  if (!chartCycles.length) {
    return (
      <AreaLineChart
        variant="plain"
        state="empty"
        emptyLabel={pt.dashboard.charts.expenseEvolutionEmptyNoCycles}
        className={className}
      />
    );
  }

  if (isError && !hasData) {
    return (
      <AreaLineChart
        variant="plain"
        state="error"
        onRetry={refetch}
        className={className}
      />
    );
  }

  if (isLoading && !hasData) {
    return <AreaLineChart variant="plain" state="loading" className={className} />;
  }

  if (!hasData) {
    return (
      <AreaLineChart
        variant="plain"
        state="empty"
        emptyLabel={pt.dashboard.charts.expenseEvolutionEmpty}
        className={className}
      />
    );
  }

  return <ExpenseTimelineChartView className={className} data={data} avg={avg} />;
}

export default ExpenseTimelineChart;
